import { HTMLAttributes } from 'react';
import styled, { css, DefaultTheme, StyledComponent, ThemeProps } from 'styled-components';
import {
    alpha,
    FOCUS_OUTLINE,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Icon,
    IconProps,
} from '@optimus-web/core';
import { Bordered, Colored, Emphasized, Highlighted, Hovered, MatrixTextVariant, Selected } from './types';

const getBorderStyle = ({ theme }: { theme: DefaultTheme }) =>
    `1px solid ${getToken('paletteBrandNight150')({ theme })}`;

const DEFAULT_SUCCESS_OPACITY_MODIFIER = 0.8;

const getCellStyle = () => css`
    border-bottom: ${getBorderStyle};
    &:not(:last-of-type) {
        border-right: ${getBorderStyle};
    }
`;

const getMatrixEmphasizedColor = ({ emphasized, theme }: ThemeProps<DefaultTheme> & Emphasized) =>
    emphasized
        ? css`
              background-color: ${alpha(
                  getToken('paletteBrandNight400')({ theme }),
                  getToken('opacity100')({ theme })
              )};
          `
        : '';

const getMatrixHighlightColor = ({ highlighted, theme }: ThemeProps<DefaultTheme> & Highlighted) =>
    highlighted
        ? css`
              background-color: ${alpha(
                  getToken('paletteBrandIndigo500')({ theme }),
                  getToken('opacity100')({ theme })
              )};
          `
        : '';

const getMatrixEmphasizedNonTransparentColor = ({ emphasized, theme }: ThemeProps<DefaultTheme> & Emphasized) =>
    emphasized
        ? css`
              background: linear-gradient(
                      ${alpha(getToken('paletteBrandNight400')({ theme }), getToken('opacity100')({ theme }))},
                      ${alpha(getToken('paletteBrandNight400')({ theme }), getToken('opacity100')({ theme }))}
                  ),
                  ${getToken('backgroundStaticFlat')};
          `
        : '';

const getMatrixHighlightedNonTransparentColor = ({ highlighted, theme }: ThemeProps<DefaultTheme> & Highlighted) =>
    highlighted
        ? css`
              background: linear-gradient(
                      ${alpha(getToken('paletteBrandNight400')({ theme }), getToken('opacity100')({ theme }))},
                      ${alpha(getToken('paletteBrandNight400')({ theme }), getToken('opacity100')({ theme }))}
                  ),
                  ${getToken('backgroundStaticFlat')};
          `
        : '';

const getMatrixCellColor = (props: ThemeProps<DefaultTheme> & Colored) => {
    if (props.cellColor) {
        const { color, intensity = 1 } = props.cellColor;
        const modifiedIntensity = Math.min(intensity, 1);
        switch (color) {
            case 'primary':
                return css`
                    background-color: ${alpha(getToken('paletteBrandIndigo300')(props), intensity)};
                `;
            case 'success':
                return css`
                    background-color: ${alpha(
                        getToken('paletteSemanticGreen800')(props),
                        modifiedIntensity * DEFAULT_SUCCESS_OPACITY_MODIFIER
                    )};
                `;
            case 'info':
                return css`
                    background-color: ${alpha(getToken('paletteSemanticBlue300')(props), intensity)};
                `;
            case 'danger':
                return css`
                    background-color: ${alpha(getToken('paletteSemanticRed300')(props), intensity)};
                `;
            case 'warning':
                return css`
                    background-color: ${alpha(getToken('paletteSemanticOrange300')(props), intensity)};
                `;
            case 'basic':
                return css`
                    background-color: ${alpha(getToken('backgroundInteractiveNeutralDefault')(props), intensity)};
                `;
            case 'successLight':
                return css`
                    background-color: ${alpha(getToken('paletteSemanticGreen200')(props), intensity)};
                `;
        }
    }
    return '';
};

export const MatrixElement = styled.table<Bordered>`
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
`;

export const ColumnDefinitionsElement = styled.colgroup``;

export interface ColumnElementProps {
    columnWidth?: string;
}

export const ColumnElement = styled.col<ColumnElementProps>`
    ${(props) =>
        props.columnWidth &&
        css`
            width: ${props.columnWidth};
        `}
`;

export interface MatrixHeadElementProps {
    fixedHeader?: boolean;
    highlighted?: boolean;
}

export const MatrixHeadElement = styled.thead<MatrixHeadElementProps>`
    ${getTextTokenPreset('bodySmallStrong')};
    color: ${getToken('textStaticPrimary')};

    ${(props) =>
        props.fixedHeader &&
        css`
            background-color: ${getToken('paletteBrandNight0')};
            position: sticky;
            top: 0;
            z-index: 2;
        `}
    ${getMatrixHighlightColor}
`;

export const MatrixBodyElement = styled.tbody<{ isLoading?: boolean }>`
    pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'initial')};
    font-size: 13px;
    color: ${getToken('textStaticPrimary')};
`;

export const MatrixHeaderRowElement = styled.tr<Bordered>`
    padding: ${getSpacingToken('300')}px 0;
    ${({ bordered }) =>
        bordered &&
        css`
            border-bottom: ${getBorderStyle};
        `}
`;

export interface MatrixHeaderCellElementProps extends Bordered, Highlighted {
    columnAlignment?: string;
    fixedFirstColumn?: boolean;
    sortable?: boolean;
}

export const MatrixHeaderCellElement = styled.th<MatrixHeaderCellElementProps>`
    ${getTextTokenPreset('bodySmall')}

    border-bottom: ${getBorderStyle};
    box-sizing: border-box;
    padding: ${getSpacingToken('200')}px;
    text-align: ${(props) => props.columnAlignment || 'left'};

    ${(props) =>
        props.fixedFirstColumn &&
        css`
            &:first-of-type {
                background-color: ${getToken('paletteBrandNight0')};
                position: sticky;
                left: 0;
            }
        `}

    ${getMatrixEmphasizedColor}
    ${getMatrixHighlightColor}

    ${(props) =>
        props.sortable &&
        css`
            cursor: pointer;
        `}

    ${(props) =>
        props.bordered &&
        css`
            border-top: ${getBorderStyle};
            ${getCellStyle}
        `}
`;

export const MatrixRowElement = styled.tr<Bordered & Selected & HTMLAttributes<HTMLTableRowElement>>`
    ${(props) =>
        props.bordered &&
        css`
            border-bottom: ${getBorderStyle};
        `}
    ${({ role }) =>
        role === 'button' &&
        css`
            &:hover {
                cursor: pointer;
                background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
            }
            ${FOCUS_OUTLINE}
        `}
    ${({ selected, theme }) =>
        selected
            ? css`
                  background-color: ${alpha(
                      getToken('paletteBrandIndigo500')({ theme }),
                      getToken('opacity100')({ theme })
                  )};
              `
            : ''}
`;

export interface MatrixCellElementProps extends Bordered, Emphasized, Highlighted, Colored, Hovered {
    depth?: number;
    columnAlignment?: string;
    columnHovered?: boolean;
    fixedFirstColumn?: boolean;
    textVariant?: MatrixTextVariant;
    $width?: string;
    selectedCell?: boolean;
    verticalAlign?: string;
}

const getMatrixTextColor = (props: ThemeProps<DefaultTheme> & Pick<MatrixCellElementProps, 'textVariant'>) => {
    switch (props.textVariant) {
        case 'disabled':
            return css`
                color: ${getToken('textDisabled')};
            `;
        case 'secondary':
            return css`
                color: ${getToken('textStaticTertiary')};
            `;
        default:
            return '';
    }
};

export const MatrixCellElement = styled.td<MatrixCellElementProps>`
    ${getTextTokenPreset('bodyMedium')}

    box-sizing: border-box;
    padding: ${getSpacingToken('200')}px;
    text-align: ${(props) => props.columnAlignment || 'left'};
    vertical-align: ${(props) => props.verticalAlign || 'middle'};
    ${(props) =>
        props.bordered &&
        css`
            ${getCellStyle}
        `}
    ${({ fixedFirstColumn, depth, theme }) =>
        fixedFirstColumn &&
        css`
            &:first-of-type {
                background-color: ${getToken('paletteBrandNight0')};
                ${getMatrixEmphasizedNonTransparentColor};
                ${getMatrixHighlightedNonTransparentColor};
                ${getMatrixCellColor}
                position: sticky;
                left: 0;
                padding-left: ${depth
                    ? `${getSpacingToken('200')({ theme }) + getSpacingToken('700')({ theme }) * depth}px`
                    : getSpacingToken('200')};
            }
        `}
    ${getMatrixTextColor}
    ${getMatrixEmphasizedColor}
    ${getMatrixHighlightColor}
    ${getMatrixCellColor}
    ${(props) =>
        props.$width !== undefined &&
        css`
            min-width: ${props.$width};
        `}
    ${({ role, columnHovered }) =>
        role === 'button' &&
        columnHovered &&
        css`
            cursor: pointer;
            background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
            ${FOCUS_OUTLINE}
        `}
    ${({ role, hovered }) =>
        role === 'button' &&
        hovered &&
        css`
            &:hover {
                cursor: pointer;
                background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
                ${FOCUS_OUTLINE}
            }
        `}
    ${(props) =>
        props.selectedCell &&
        css`
            box-shadow: inset 0 0 0 2px ${getToken('paletteBrandNight900')};
        `}
`;

const iconStyle = css`
    color: ${getToken('textStaticPrimary')};
    vertical-align: text-bottom;
`;

export const MatrixFooterElement = styled.tfoot``;

export const SortAscendingIcon = styled(Icon)`
    ${iconStyle}
` as StyledComponent<'span', DefaultTheme, Omit<IconProps, 'value'> & { value?: string }>;
SortAscendingIcon.defaultProps = { value: 'arrow_upward' };

export const SortDescendingIcon = styled(Icon)`
    ${iconStyle}
` as StyledComponent<'span', DefaultTheme, Omit<IconProps, 'value'> & { value?: string }>;
SortDescendingIcon.defaultProps = { value: 'arrow_downward' };

interface MatrixWrapperProps extends Bordered {
    maxHeight?: string;
    maxWidth?: string;
}

export const MatrixWrapper = styled.div<MatrixWrapperProps>`
    overflow-x: auto;
    position: relative;

    ${(props) =>
        props.maxHeight &&
        css`
            max-height: ${props.maxHeight};
        `}
    ${(props) =>
        props.maxWidth &&
        css`
            max-width: ${props.maxWidth};
        `}
    ${({ bordered }) =>
        bordered &&
        css`
            border-bottom: ${getBorderStyle};
            border-left: ${getBorderStyle};
            border-right: ${getBorderStyle};
        `}
`;
